import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import Recaptcha from '../../components/common/Recaptcha';
import PublicNav from '../../components/layout/PublicNav';
import PublicFooter from '../../components/layout/PublicFooter';
import logo from '../../assets/logo.png';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone_number: '', password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await authService.register({ ...form, recaptcha_token: captchaToken });
      localStorage.setItem('access_token', data.data.tokens.access);
      localStorage.setItem('refresh_token', data.data.tokens.refresh);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const strength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', '#FF4757', '#FF9900', '#f5a623', '#00C896'][strength];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PublicNav />
      <div className="auth-bg" style={{ alignItems: 'flex-start', paddingTop: '3rem', flex: 1, minHeight: 0 }}>
      <div style={{ width: '100%', maxWidth: '500px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
            <img src={logo} alt="PCredit" style={{ height: 40, width: 'auto' }} />
          </div>
          <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>Create your account</p>
        </div>

        <div className="auth-card" style={{ maxWidth: '500px' }}>
          {error && (
            <div className="alert alert-error">
              <span>⚠</span><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">First name</label>
                <input className="form-input" name="first_name" value={form.first_name} onChange={handleChange} required placeholder="John" />
              </div>
              <div className="form-group">
                <label className="form-label">Last name</label>
                <input className="form-input" name="last_name" value={form.last_name} onChange={handleChange} required placeholder="Doe" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email address</label>
              <input className="form-input" type="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Phone number</label>
              <input className="form-input" type="tel" name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="+2348012345678" />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required minLength={8}
                  placeholder="Min 8 characters"
                  style={{ paddingRight: '2.75rem' }}
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', fontSize: '1rem' }}>
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
              {form.password && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{
                        flex: 1, height: 3, borderRadius: 99,
                        background: i <= strength ? strengthColor : 'var(--border-bright)',
                        transition: 'background .2s',
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: strengthColor, fontWeight: 600 }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm password</label>
              <input
                className="form-input"
                type={showPass ? 'text' : 'password'}
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                required
                placeholder="Repeat password"
              />
              {form.confirm_password && form.password !== form.confirm_password && (
                <span className="form-error">Passwords don't match</span>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0' }}>
              <Recaptcha onChange={setCaptchaToken} />
            </div>

            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading || !captchaToken}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin .6s linear infinite', display: 'inline-block' }} />
                  Creating account…
                </span>
              ) : 'Create account'}
            </button>
          </form>

          <div className="glow-divider" style={{ margin: '1.5rem 0' }} />

          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-2)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ fontWeight: 600 }}>Sign in</Link>
          </p>
        </div>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>
          Protected by 256-bit SSL encryption
        </p>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}
